import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as Pencil } from '@/assets/icons/pencil-with-background.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import { Topics, TopicsList } from '@/components/Table';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import { Role, UnassignedVideoResource } from '@/types/resources';
import { numFormatter } from '@/utils/helpers';

import AttachmentModal from './AttachmentModal';
import StatusButton from './StatusButton';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 192px;
`;

const ViewsCount = styled(Text)`
  margin: 8px 0;
`;

export interface AttachmentModalState {
  isOpen: boolean;
  category: string;
}

interface Props {
  video: UnassignedVideoResource;
  videosQueryKey: Array<any>;
  userRole: Role;
}

const AssignTopicsTableRow: FC<Props> = ({
  video,
  videosQueryKey,
  userRole,
}) => {
  const [attachmentModalState, setAttachmentModalState] =
    useState<AttachmentModalState>({ isOpen: false, category: '' });

  const scriptTopics = video.topics.filter(
    (topic) => topic.category === 'script',
  );

  const thumbTopics = video.topics.filter(
    (topic) => topic.category === 'thumb',
  );

  const titleTopics = video.topics.filter(
    (topic) => topic.category === 'title',
  );

  const typeTopics = video.topics.filter((topic) => topic.category === 'type');

  return (
    <tr>
      <Td>
        <Thumbnail src={video.thumbnail} />
      </Td>
      <Td>
        <Text
          as="a"
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          $type="smallBold"
        >
          {video.title}
        </Text>
        <ViewsCount $type="smallMedium">{numFormatter(video.views)}</ViewsCount>
        <Text
          as="a"
          href={video.channelUrl}
          target="_blank"
          $type="extraSmall"
          $color="gray"
          rel="noopener noreferrer"
        >
          {video.channelName}
        </Text>
      </Td>
      <Td>
        <Topics $hasTopics={!!scriptTopics.length}>
          <Pencil />
          {!!scriptTopics.length ? (
            <TopicsList>
              {scriptTopics.map((topic) => (
                <Tag key={topic.id}>{topic.title}</Tag>
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'script',
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </Td>
      <Td>
        <Topics $hasTopics={!!thumbTopics.length}>
          <Pencil />
          {!!thumbTopics.length ? (
            <TopicsList>
              {thumbTopics.map((topic) => (
                <Tag key={topic.id}>{topic.title}</Tag>
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'thumb',
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </Td>
      <Td>
        <Topics $hasTopics={!!titleTopics.length}>
          <Pencil />
          {!!titleTopics.length ? (
            <TopicsList>
              {titleTopics.map((topic) => (
                <Tag key={topic.id}>{topic.title}</Tag>
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'title',
                })
              }
            >
              Assign topics
            </Text>
          )}
        </Topics>
      </Td>
      <Td>
        <Topics $hasTopics={!!typeTopics.length}>
          <Pencil />
          {!!typeTopics.length ? (
            <TopicsList>
              {typeTopics.map((type) => (
                <Tag key={type.id}>{type.title}</Tag>
              ))}
            </TopicsList>
          ) : (
            <Text
              $color="gray"
              $type="small"
              as="button"
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'type',
                })
              }
            >
              Assign types
            </Text>
          )}
        </Topics>
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'script',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!scriptTopics.length
                ? 'Edit script topics'
                : 'Assign script topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'thumb',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics.length
                ? 'Edit thumb topics'
                : 'Assign thumb topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'title',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics.length
                ? 'Edit title topics'
                : 'Assign title topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'type',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!typeTopics.length ? 'Edit types' : 'Assign types'}
            </ActionsDropdownButton>
          </StyledActionsDropdown>
          <StatusButton status={video.topicsStatus} videoId={video.id} />
          <AnimatePresence>
            {attachmentModalState.isOpen && (
              <AttachmentModal
                videosQueryKey={videosQueryKey}
                onClose={() =>
                  setAttachmentModalState({ isOpen: false, category: '' })
                }
                video={video}
                attachmentModalState={attachmentModalState}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default AssignTopicsTableRow;
